import { ref, readonly } from 'vue';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: number;
  message: string;
  type: ToastType;
  duration: number;
}

// Create a single instance of the toast state that will be shared across the app
const toasts = ref<Toast[]>([]);
let nextId = 1;

/**
 * Composable for managing toast notifications throughout the application
 * Provides a centralized way to show success, error, warning, and info messages to users
 */
export const useToast = () => {
  /**
   * Show a toast notification
   * @param message - The message to display
   * @param type - Type of toast (success, error, warning, info)
   * @param duration - How long to show the toast in milliseconds (default: 4000)
   */
  const showToast = (message: string, type: ToastType = 'info', duration: number = 4000) => {
    const id = nextId++;
    const toast: Toast = { id, message, type, duration };
    
    toasts.value.push(toast);
    
    // Auto-dismiss after duration
    if (duration > 0) {
      setTimeout(() => {
        dismissToast(id);
      }, duration);
    }
    
    return id;
  };

  /**
   * Show a success toast notification
   * @param message - The success message to display
   * @param duration - How long to show the toast in milliseconds (default: 4000)
   */
  const showSuccess = (message: string, duration?: number) => {
    return showToast(message, 'success', duration);
  };

  /**
   * Show an error toast notification
   * @param message - The error message to display
   * @param duration - How long to show the toast in milliseconds (default: 5000)
   */
  const showError = (message: string, duration: number = 5000) => {
    return showToast(message, 'error', duration);
  };

  /**
   * Show a warning toast notification
   * @param message - The warning message to display
   * @param duration - How long to show the toast in milliseconds (default: 4000)
   */
  const showWarning = (message: string, duration?: number) => {
    return showToast(message, 'warning', duration);
  };

  /**
   * Show an info toast notification
   * @param message - The info message to display
   * @param duration - How long to show the toast in milliseconds (default: 4000)
   */
  const showInfo = (message: string, duration?: number) => {
    return showToast(message, 'info', duration);
  };

  /**
   * Dismiss a specific toast by ID
   * @param id - The ID of the toast to dismiss
   */
  const dismissToast = (id: number) => {
    const index = toasts.value.findIndex(t => t.id === id);
    if (index !== -1) {
      toasts.value.splice(index, 1);
    }
  };

  /**
   * Clear all toasts
   */
  const clearAllToasts = () => {
    toasts.value = [];
  };

  return {
    toasts: readonly(toasts),
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    dismissToast,
    clearAllToasts
  };
};
